import type {
  Book,
  ExperienceGroup,
  HeroStat,
  Honor,
  Pillar,
} from '../types/profile'

export const heroStats: HeroStat[] = [
  {
    value: '$200B+',
    label: 'Enterprise Budget Managed',
    detail: 'Department of the Navy',
  },
  {
    value: '800,000+',
    label: 'Military & Civilian Personnel Led',
    detail: 'Navy and Marine Corps enterprise',
  },
  {
    value: '45+ Years',
    label: 'Executive Leadership',
    detail: 'Defense, government and private enterprise',
  },
]

export const pillars: Pillar[] = [
  {
    id: 'operations',
    title: 'Enterprise Operations & Governance',
    description:
      'Chief operating authority for one of the largest enterprises in the world, now applied in the boardroom to audit discipline, regulatory readiness and executive oversight.',
    proof: ['Pentagon COO', 'Board Director, NOVUS Capital'],
  },
  {
    id: 'defense',
    title: 'National Security & Defense Strategy',
    description:
      'Three decades of command-level judgment on force readiness, manpower policy and national defense strategy at the most senior levels of the Pentagon.',
    proof: ['Rear Admiral, U.S. Navy', 'Pentagon senior leadership'],
  },
  {
    id: 'policy',
    title: 'Public Policy & Legislative Strategy',
    description:
      'Chief of Staff in both chambers of Congress, translating complex policy and appropriations strategy into durable legislative outcomes.',
    proof: ['U.S. Senate Chief of Staff', 'U.S. House Chief of Staff'],
  },
  {
    id: 'crisis',
    title: 'Crisis Management & Strategic Communications',
    description:
      'Led strategic communications in an active combat theater and for national institutions under intense public and media scrutiny.',
    proof: ['MNF-I Baghdad spokesperson', 'Award-winning public affairs'],
  },
]

export const experienceGroups: ExperienceGroup[] = [
  {
    id: 'board',
    label: 'Board & Corporate Governance',
    headline:
      'Independent board judgment shaped by enterprise-scale fiduciary and regulatory responsibility.',
    entries: [
      {
        organization: 'NOVUS Capital',
        role: 'Board of Directors',
        period: 'Current',
        summary:
          'Serves on the board of a banking and capital institution, contributing oversight across regulatory readiness, audit and governance practice.',
        metrics: ['Banking', 'Regulatory Readiness', 'Audit & Governance'],
      },
      {
        organization: 'CRC Surface Technologies',
        role: 'Vice Chairman, Board of Directors',
        period: 'Former',
        summary:
          'Provided vice-chair leadership through growth and operational transformation for an industrial technologies enterprise.',
        metrics: ['Industrial Operations', 'Growth Strategy'],
      },
      {
        organization: 'Avalon Correctional Services',
        role: 'Board of Directors',
        period: 'Former',
        summary:
          'Directed governance oversight for a publicly traded operator in a highly regulated, compliance-intensive sector.',
        metrics: ['Public Company', 'Compliance Oversight'],
      },
    ],
  },
  {
    id: 'federal',
    label: 'Federal Executive Leadership',
    headline:
      'Senate-confirmed executive authority at the operational center of the Department of the Navy.',
    entries: [
      {
        organization: 'Department of the Navy',
        role: 'Acting Under Secretary of the Navy & Chief Operating Officer',
        period: 'Pentagon',
        summary:
          'Served as the department’s chief operating officer, directing enterprise operations, business transformation and management of the Navy and Marine Corps.',
        metrics: ['$200B+ budget', '800,000+ personnel'],
      },
      {
        organization: 'Department of the Navy',
        role: '18th Assistant Secretary of the Navy (Manpower & Reserve Affairs)',
        period: 'Senate-Confirmed',
        summary:
          'Set total force policy for recruiting, retention, compensation, health of the force and civilian workforce strategy across the department.',
        metrics: ['Total force policy', 'Senate confirmation'],
      },
      {
        organization: 'U.S. Senate & U.S. House of Representatives',
        role: 'Chief of Staff to Senator James Lankford',
        period: 'Capitol Hill',
        summary:
          'Ran congressional operations in both chambers, directing staff, legislative strategy, appropriations priorities and constituent enterprise.',
        metrics: ['Both chambers', 'Legislative strategy'],
      },
    ],
  },
  {
    id: 'state',
    label: 'State Agency Leadership & Public Policy',
    headline:
      'Turnaround and stewardship leadership of large, multi-facility public agencies.',
    entries: [
      {
        organization: 'Oklahoma Department of Mental Health & Substance Abuse Services',
        role: 'Interim Commissioner',
        period: 'State of Oklahoma',
        summary:
          'Stabilized agency operations, finances and public trust across a statewide behavioral health system during a period of intense scrutiny.',
        metrics: ['$650M+ budget', '2,000+ staff', '11 facilities'],
      },
      {
        organization: 'Oklahoma Department of Veterans Affairs',
        role: 'Executive Director',
        period: 'State of Oklahoma',
        summary:
          'Directed statewide veterans services and long-term care operations, elevating standards of care, accountability and workforce performance.',
        metrics: ['$160M budget', '1,400+ staff', '7 facilities'],
      },
    ],
  },
  {
    id: 'military',
    label: 'Military Command & Global Operations',
    headline:
      'Flag-level command across 35+ years of active and reserve service, including combat theater leadership.',
    entries: [
      {
        organization: 'U.S. Navy',
        role: 'Rear Admiral — Deputy Chief of Information & Reserve Public Affairs Director',
        period: '35+ years of service',
        summary:
          'Led the Navy’s reserve public affairs community and served as deputy for information, shaping the service’s global communications posture.',
        metrics: ['Flag officer', 'Global communications'],
      },
      {
        organization: 'Multi-National Force – Iraq, Baghdad',
        role: 'Director of Strategic Communications',
        period: 'Combat theater',
        summary:
          'Directed coalition strategic communications during high-stakes combat operations and defining historical moments, including the arraignment of Saddam Hussein.',
        metrics: ['Coalition command', 'Crisis communications'],
      },
    ],
  },
]

export const books: Book[] = [
  {
    title: 'Charging a Tyrant',
    subtitle: 'The Arraignment of Saddam Hussein',
    description:
      'A firsthand account of the coalition effort behind one of the most consequential legal proceedings of the modern era.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/b9948477-5238-4d76-9d22-37892569fd3b.jpg',
    year: 'Leadership & History',
  },
  {
    title: 'Profiles in Patriotic Leadership',
    subtitle: 'Lessons from service and command',
    description:
      'Portraits of leaders whose decisions under pressure define the standards of character-driven executive leadership.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/e07a4973-8ef1-479a-ac24-22bf01559a0c.jpg',
    year: 'Leadership',
  },
  {
    title: 'The Jim Thorpe Award',
    subtitle: 'The First 20 Years',
    description:
      'The definitive history of an American athletic institution and the stewardship that built it.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/c80684b0-f286-4576-91a0-2175b3b8727c.jpg',
    year: 'History',
  },
]

export const honors: Honor[] = [
  { name: 'Legion of Merit', issuer: 'United States Navy' },
  { name: 'Bronze Star Medal (2x)', issuer: 'United States Navy' },
  {
    name: 'Distinguished Public Service Award',
    issuer: 'Secretary of the Navy',
  },
  {
    name: 'Medal for Outstanding Public Service',
    issuer: 'Secretary of Defense',
  },
  { name: 'Oklahoma Military Hall of Honor', issuer: 'Inductee' },
  { name: '50 Most Influential Oklahomans', issuer: 'Statewide recognition' },
]

export const inquiryTypes = [
  'Board Directorship',
  'Keynote Speaking',
  'Strategic Advisory',
] as const

export const heroImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/8c1a6131-7ef1-41b7-97f4-473bfff7e58f.jpg'
